<template>
  <SideMenuDrawer
    :open="open"
    :disabled="hasLoadingOrPending"
    :mobile="mobile"
    class="campaign-sidebar"
    @update:open="emit('update:open', $event)"
  >
    <SideMenuNew size="2">
      <!-- Summary — always visible -->
      <MenuItem :link="routeResolver.summary(campaignGroupId).path">
        <MenuIcon :icon="CampaignIcon" />
        <MenuLabel>{{ $t('modules.campaigns.layout.menu.summary') }}</MenuLabel>
        <Badge
          v-if="isDirty"
          interactive
          :aria-label="$t('modules.campaigns.layout.header.save')"
          variant="filled"
          color="yellow"
          size="3"
          square
          radius="full"
          :loading="campaignPageIsSaving"
          :icon="SaveIcon"
          @click.stop.prevent="emit('save')"
        />
      </MenuItem>

      <MenuDivider />

      <!-- Targeting -->
      <MenuItem
        :is-open="isTargetingOpen"
        :variant="!hasLoading && !hasTargetGroups ? 'attention' : undefined"
        :disabled="hasLoadingOrPending"
      >
        <MenuIcon :icon="BulsyeyIcon" />
        <MenuLabel v-if="hasLoading"><Skeleton width="100px" /></MenuLabel>
        <MenuLabel v-else>{{ targetingLabel }}</MenuLabel>
        <MenuEnd>
          <MenuAction>
            <Button
              :icon="PlusIcon"
              :variant="hasTargetGroups ? 'outline' : 'surface'"
              square
              size="1"
              color="default"
              :disabled="hasLoadingOrPending || isAddingTarget"
              :loading="isAddingTarget"
              :aria-label="$t('modules.campaigns.layout.menu.addTargetGroup')"
              @click.stop="handleAddTargetClick"
            />
          </MenuAction>
          <Badge v-if="hasTargetGroups" variant="soft" size="3">{{ targetingCount }}</Badge>
        </MenuEnd>
        <template #children>
          <MenuSubItem
            v-for="tg in allTargetGroups"
            :key="tg.id"
            :link="routeResolver.targetGroup(campaignGroupId, tg.id).path"
          >
            <MenuIcon :icon="BulsyeyIcon" />
            <MenuLabel>{{ tg.name }}</MenuLabel>
            <MenuEnd>
              <MenuAction class="campaign-menu__entity-actions" @click.stop.prevent>
                <ButtonMenu
                  :items="getTargetGroupMenuItems(tg)"
                  :label="$t('modules.campaigns.layout.menu.targetGroupActions')"
                  :icon="MoreVerticalIcon"
                  :chevron="false"
                  placement="bottom-end"
                  variant="outline"
                  square
                  size="1"
                  :disabled="hasLoadingOrPending"
                  :loading="copyingTargetGroupId === tg.id || deletingTargetGroupId === tg.id"
                />
              </MenuAction>
              <Tooltip
                v-if="targetConflictFlags.targetGroupIds.has(tg.id)"
                :text="$t('modules.campaigns.hints.targetGroup.blocksPricingRule')"
                placement="top-start"
                max-width="5"
              >
                <Badge
                  :aria-label="$t('modules.campaigns.layout.menu.blocksPricingRule')"
                  :variant="isTargetGroupActive(tg.id) ? 'filled' : 'soft'"
                  color="yellow"
                  size="3"
                  square
                  radius="full"
                  :icon="WarningIcon"
                />
              </Tooltip>
              <Badge
                v-if="isItemDirty('target', tg.id)"
                interactive
                :aria-label="$t('modules.campaigns.layout.header.save')"
                variant="filled"
                color="yellow"
                size="3"
                square
                radius="full"
                :loading="campaignPageIsSaving"
                :icon="SaveIcon"
                @click.stop.prevent="emit('save')"
              />
            </MenuEnd>
          </MenuSubItem>
        </template>
      </MenuItem>

      <!-- Creatives -->
      <MenuItem
        :is-open="isCreativesOpen"
        :variant="!isSummaryLoading && !hasCreativeGroups ? 'attention' : undefined"
        :disabled="isSummaryLoading"
      >
        <MenuIcon :icon="ImageSquaredIcon" />
        <MenuLabel v-if="isSummaryLoading"><Skeleton width="90px" /></MenuLabel>
        <MenuLabel v-else>{{ creativesLabel }}</MenuLabel>
        <MenuEnd>
          <MenuAction>
            <span @click.stop>
              <ButtonMenu
                :items="creativeTypeItems"
                :icon="PlusIcon"
                :variant="hasCreativeGroups ? 'outline' : 'surface'"
                size="1"
                menu-width="280px"
                color="default"
                :disabled="isSummaryLoading || isCreatingCreative"
                :loading="isCreatingCreative"
              />
            </span>
          </MenuAction>
          <Badge v-if="hasCreativeGroups" variant="soft" size="3">{{ creativesCount }}</Badge>
        </MenuEnd>
        <template #children>
          <MenuSubItem
            v-for="cg in allCreativeGroups"
            :key="cg.id"
            :link="routeResolver.creativeGroup(campaignGroupId, cg.id).path"
          >
            <MenuIcon :icon="ImageSquaredIcon" />
            <MenuLabel>{{ cg.name }}</MenuLabel>
            <MenuEnd>
              <MenuAction class="campaign-menu__entity-actions" @click.stop.prevent>
                <ButtonMenu
                  :items="getCreativeGroupMenuItems(cg)"
                  :label="$t('modules.campaigns.layout.menu.creativeGroupActions')"
                  :icon="MoreVerticalIcon"
                  :chevron="false"
                  placement="bottom-end"
                  variant="outline"
                  square
                  size="1"
                  :disabled="hasLoadingOrPending"
                  :loading="deletingCreativeGroupId === cg.id"
                />
              </MenuAction>
              <Badge
                v-if="isItemDirty('creative', cg.id)"
                interactive
                :aria-label="$t('modules.campaigns.layout.header.save')"
                variant="filled"
                color="yellow"
                size="3"
                square
                radius="full"
                :loading="campaignPageIsSaving"
                :icon="SaveIcon"
                @click.stop.prevent="emit('save')"
              />
            </MenuEnd>
          </MenuSubItem>
        </template>
      </MenuItem>

      <!-- Ads -->
      <MenuItem
        :is-open="isAdsOpen"
        :variant="!isSummaryLoading && !hasAdGroups ? 'attention' : undefined"
        :disabled="isSummaryLoading"
      >
        <MenuIcon :icon="PencilRulerIcon" />
        <MenuLabel v-if="isSummaryLoading"><Skeleton width="60px" /></MenuLabel>
        <MenuLabel v-else>{{ adsLabel }}</MenuLabel>
        <MenuEnd>
          <MenuAction>
            <Tooltip :text="addAdDisabledReason" :disabled="!addAdDisabledReason">
              <Button
                :icon="PlusIcon"
                :variant="hasAdGroups ? 'outline' : 'surface'"
                square
                size="1"
                color="default"
                :disabled="isSummaryLoading || isAddingAdGroup || !canAddAd"
                :loading="isAddingAdGroup"
                :aria-label="$t('modules.campaigns.layout.menu.addAdGroup')"
                @click.stop="handleAddAdClick"
              />
            </Tooltip>
          </MenuAction>
          <Badge v-if="hasAdGroups" variant="soft" size="3">{{ adsCount }}</Badge>
        </MenuEnd>
        <template #children>
          <MenuSubItem
            v-for="ag in allAdGroups"
            :key="ag.id"
            :link="routeResolver.adGroup(campaignGroupId, ag.id).path"
          >
            <MenuIcon :icon="PencilRulerIcon" />
            <MenuLabel>{{ ag.name }}</MenuLabel>
            <MenuEnd>
              <MenuAction class="campaign-menu__entity-actions" @click.stop.prevent>
                <ButtonMenu
                  :items="getAdGroupMenuItems(ag)"
                  :label="$t('modules.campaigns.layout.menu.adGroupActions')"
                  :icon="MoreVerticalIcon"
                  :chevron="false"
                  placement="bottom-end"
                  variant="outline"
                  square
                  size="1"
                  :disabled="hasLoadingOrPending"
                  :loading="copyingAdGroupId === ag.id || deletingAdGroupId === ag.id"
                />
              </MenuAction>
              <Tooltip
                v-if="targetConflictFlags.adGroupIds.has(ag.id)"
                :text="$t('modules.campaigns.hints.adGroup.blockedByTarget')"
                placement="top-start"
                max-width="5"
              >
                <Badge
                  :aria-label="$t('modules.campaigns.layout.menu.blockedByTarget')"
                  :variant="isAdGroupActive(ag.id) ? 'filled' : 'soft'"
                  color="yellow"
                  size="3"
                  square
                  radius="full"
                  :icon="WarningIcon"
                />
              </Tooltip>
              <Badge
                v-if="isItemDirty('ad', ag.id)"
                interactive
                :aria-label="$t('modules.campaigns.layout.header.save')"
                variant="filled"
                color="yellow"
                size="3"
                square
                radius="full"
                :loading="campaignPageIsSaving"
                :icon="SaveIcon"
                @click.stop.prevent="emit('save')"
              />
            </MenuEnd>
          </MenuSubItem>
        </template>
      </MenuItem>
    </SideMenuNew>
  </SideMenuDrawer>
</template>

<script setup lang="ts">
import type { AdGroupListView } from '@hprofits/api/ad-group/types';
import type { CreativeGroupListItem } from '@hprofits/api/creative-group/types';
import type { TargetGroupListView } from '@hprofits/api/target-group/types';
import type { CampaignGroupId } from '@hprofits/shared';
import {
  Badge,
  BulsyeyIcon,
  Button,
  ButtonMenu,
  type ButtonMenuItem,
  CampaignIcon,
  CopyIcon,
  ImageSquaredIcon,
  MenuAction,
  MenuDivider,
  MenuEnd,
  MenuIcon,
  MenuItem,
  MenuLabel,
  MenuSubItem,
  MoreVerticalIcon,
  PencilRulerIcon,
  PlusIcon,
  SaveIcon,
  SideMenuNew,
  Skeleton,
  Tooltip,
  WarningIcon,
} from '@hprofits/ui';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import SideMenuDrawer from '@/components/Layout/SideMenuDrawer.vue';
import { t } from '@/i18n';
import { useCampaignGroupContext } from '@/modules/campaigns/composables/context/campaignGroupContextProvider';
import type { DirtyItemType } from '@/modules/campaigns/composables/mutations/types';
import { useTargetConflictFlags } from '@/modules/campaigns/composables/useTargetConflictFlags';
import { buildMenuItemDelete } from '@/modules/common/menu';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';

defineOptions({ name: 'CampaignMenu' });

const props = defineProps<{
  campaignGroupId: CampaignGroupId;
  open: boolean;
  mobile: boolean;
  isDirty: boolean;
  campaignPageIsSaving: boolean;
  isItemDirty: (type: DirtyItemType, id: string | number) => boolean;
  creativeTypeItems: ReadonlyArray<ButtonMenuItem>;
  isCreatingCreative: boolean;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  save: [];
}>();

const route = useRoute();
const routeResolver = useRouteResolverCampaigns();

const {
  campaignGroupSource: {
    state: { data },
  },
  state: { hasLoading, hasLoadingOrPending },
  actions: {
    createTargetGroup,
    copyTargetGroup,
    createAdGroup,
    copyAdGroup,
    deleteTargetGroup,
    deleteCreativeGroup,
    deleteAdGroup,
  },
  isCreatingTargetGroup,
  copyingTargetGroupId,
  copyingAdGroupId,
  entityDeletion,
} = useCampaignGroupContext();

const targetConflictFlags = useTargetConflictFlags();

const allTargetGroups = computed(() => data.value?.targetGroups ?? []);
const allCreativeGroups = computed(() => data.value?.creativeGroups ?? []);
const allAdGroups = computed(() => data.value?.adGroups ?? []);
const { deletingTargetGroupId, deletingCreativeGroupId, deletingAdGroupId } = entityDeletion;

const targetingCount = computed(() => allTargetGroups.value.length);
const creativesCount = computed(() => allCreativeGroups.value.length);
const adsCount = computed(() => allAdGroups.value.length);

const hasTargetGroups = computed(() => targetingCount.value > 0);
const hasCreativeGroups = computed(() => creativesCount.value > 0);
const hasAdGroups = computed(() => adsCount.value > 0);

// Computed, not plain consts: a `const` in setup resolves once, so it would pin the section
// titles to the language the component mounted in and never follow a locale change.
const targetingLabel = computed(() => t('modules.campaigns.layout.menu.targeting'));
const creativesLabel = computed(() => t('modules.campaigns.layout.menu.creatives'));
const adsLabel = computed(() => t('modules.campaigns.layout.menu.ads'));

const isRouteInTargeting = computed(() => route.path.includes('/targets/'));
const isRouteInCreatives = computed(() => route.path.includes('/creatives/'));
const isRouteInAds = computed(() => route.path.includes('/ads/'));

const isTargetGroupActive = (targetGroupId: TargetGroupListView['id']): boolean =>
  route.path === routeResolver.targetGroup(props.campaignGroupId, targetGroupId).path;

const isAdGroupActive = (adGroupId: AdGroupListView['id']): boolean =>
  route.path === routeResolver.adGroup(props.campaignGroupId, adGroupId).path;

const isTargetingOpen = computed(() => hasTargetGroups.value || isRouteInTargeting.value);
const isCreativesOpen = computed(() => hasCreativeGroups.value || isRouteInCreatives.value);
const isAdsOpen = computed(() => hasAdGroups.value || isRouteInAds.value);

const isAddingTarget = isCreatingTargetGroup;
// Ad-group create opens a dialog (useAdGroupCreateDialog), so the '+' has no
// inline pending — busy lives in the dialog's own submit.
const isAddingAdGroup = computed(() => false);
// TODO: Wire to campaign-group load once the creatives/ads sections need a skeleton.
const isSummaryLoading = computed(() => false);

// The create-ad-group dialog's pricing rule requires a target group and a
// creative group to select — without both, the dialog can't be completed.
const canAddAd = computed(() => hasTargetGroups.value && hasCreativeGroups.value);
const addAdDisabledReason = computed(() =>
  !isSummaryLoading.value && !isAddingAdGroup.value && !canAddAd.value
    ? 'Please create targets & creatives before creating ads'
    : undefined,
);

const handleAddTargetClick = async (): Promise<void> => {
  await createTargetGroup();
};

const handleCopyTargetClick = async (targetGroup: TargetGroupListView): Promise<void> => {
  await copyTargetGroup({ id: targetGroup.id, name: targetGroup.name });
};

const getTargetGroupMenuItems = (targetGroup: TargetGroupListView): Array<ButtonMenuItem> => {
  const isDeleteLocked = entityDeletion.isTargetGroupLocked(targetGroup.id);
  return [
    {
      label: t('modules.campaigns.layout.menu.duplicateTargetGroup'),
      icon: CopyIcon,
      disabled: hasLoadingOrPending.value,
      onClick: () => handleCopyTargetClick(targetGroup),
    },
    {
      ...buildMenuItemDelete({
        disabled: hasLoadingOrPending.value || isDeleteLocked,
        onClick: () => deleteTargetGroup(targetGroup),
      }),
      tooltip: isDeleteLocked ? t('modules.campaigns.targetGroup.header.lockedDeleteTooltip') : undefined,
      divider: true,
    },
  ];
};

const getCreativeGroupMenuItems = (creativeGroup: CreativeGroupListItem): Array<ButtonMenuItem> => [
  buildMenuItemDelete({
    disabled: hasLoadingOrPending.value || entityDeletion.isCreativeGroupLocked(creativeGroup.id),
    onClick: () => deleteCreativeGroup(creativeGroup),
  }),
];

const handleCopyAdClick = async (adGroup: AdGroupListView): Promise<void> => {
  await copyAdGroup({ id: adGroup.id, name: adGroup.name });
};

const getAdGroupMenuItems = (adGroup: AdGroupListView): Array<ButtonMenuItem> => [
  {
    label: t('modules.campaigns.layout.menu.duplicateAdGroup'),
    icon: CopyIcon,
    disabled: hasLoadingOrPending.value,
    onClick: () => handleCopyAdClick(adGroup),
  },
  {
    ...buildMenuItemDelete({
      disabled: hasLoadingOrPending.value,
      onClick: () => deleteAdGroup(adGroup),
    }),
    divider: true,
  },
];

const handleAddAdClick = async (): Promise<void> => {
  await createAdGroup();
};
</script>

<style lang="scss">
.campaign-menu__entity-actions {
  .hp-button__text {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

.hp-side-menu__child.is-active .campaign-menu__entity-actions .hp-button-menu {
  --surface-fg: var(--filled-fg);
  --surface-border: var(--side-menu-active-tree);
  --surface-bg-soft: color-mix(in srgb, var(--filled-fg) 12%, transparent);
}
</style>
